import { routePath } from 'config/route-config';

import { directSalesMachineConfig } from 'machines/direct-sales-machine';
import { moveInMachineConfig } from 'machines/move-in-machine';
import { quoteByEmailMachineConfig } from 'machines/quote-by-email-machine';
import { voiceSalesAcquisitionMachineConfig } from 'machines/voice-sales-aquisition-machine';
import { voiceSalesWinbackMachineConfig } from 'machines/voice-sales-winback-machine';
import { voiceSalesHomeMoveMachineConfig } from 'machines/voice-sales-homemove-machine';
import { nonDomesticTransferMachineConfig } from 'machines/non-domestic-transfer-machine';
import { tariffInformationMachineConfig } from 'machines/tariff-information-machine';
import { solarRegistrationMachineConfig } from 'machines/solar-registration-machine';
import { abandonedCartMachineConfig } from 'machines/abandoned-cart-machine';
import { saleHandoffMachineConfig } from 'machines/sale-handoff-machine';

const { VITE_PATH_PREFIX, VITE_BRANCH, VITE_API_URL } = import.meta.env;
const logoPath = VITE_BRANCH
  ? `${VITE_PATH_PREFIX}/${VITE_BRANCH}`
  : VITE_PATH_PREFIX;

export const appConfig = {
  APP_NAME: 'British Gas',
  API_URL: VITE_API_URL || ''
};

export const AUTH_TOKENS = {
  NEP: 'cognito_id_token',
  UUA: 'access_token'
};

export const LIDL_COOKIE = 'energy-partner-fe';

export const AUTH_TOKEN_POOLING_INTERVAL = 1000; // 1sec

export const RELATIONSHIP_TO_PROPERTY = {
  TENANT: 'Tenant',
  HOME_OWNER: 'Homeowner'
};

export const PAYMENT_METHODS = Object.freeze({
  MONTHLY_DIRECT_DEBIT: 'mdd',
  QUATERLY_VARIABLE_DIRECT_DEBIT: 'qvdd',
  CHECK: 'qcc',
  PREPAY: 'ppm'
});

export const PAYMENT_NAMES = Object.freeze({
  [PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT]: 'Direct Debit',
  [PAYMENT_METHODS.QUATERLY_VARIABLE_DIRECT_DEBIT]:
    'Quarterly Variable Direct Debit',
  [PAYMENT_METHODS.CHECK]: 'Pay on receipt of your bill',
  [PAYMENT_METHODS.PREPAY]: 'Prepayment'
});

export const PAYMENT_METHODS_NEP = {
  [PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT]: 'DirectDebit',
  [PAYMENT_METHODS.QUATERLY_VARIABLE_DIRECT_DEBIT]: 'VariableDirectDebit',
  [PAYMENT_METHODS.CHECK]: 'OnDemand',
  [PAYMENT_METHODS.PREPAY]: 'Prepayment'
};

export const FUEL_OPTIONS = Object.freeze({
  GAS: 'gas',
  ELECTRIC: 'electricity',
  DUAL: 'dual'
});

export const FUEL_TYPES = Object.freeze({
  ELECTRIC: 'electric',
  GAS: 'gas'
});

export const CONSUMPTION_TYPES = Object.freeze({
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  ACTUAL: 'actual'
});

export const CONSUMPTION_CONFIG = {
  [FUEL_TYPES.GAS]: {
    [CONSUMPTION_TYPES.LOW]: 7500,
    [CONSUMPTION_TYPES.MEDIUM]: 11500,
    [CONSUMPTION_TYPES.HIGH]: 17000
  },
  [FUEL_TYPES.ELECTRIC]: {
    [CONSUMPTION_TYPES.LOW]: 1800,
    [CONSUMPTION_TYPES.MEDIUM]: 2700,
    [CONSUMPTION_TYPES.HIGH]: 4100
  },
  economy7: {
    [CONSUMPTION_TYPES.LOW]: 2200,
    [CONSUMPTION_TYPES.MEDIUM]: 3900,
    [CONSUMPTION_TYPES.HIGH]: 6700
  }
};

export const FUEL_NAMES = {
  [FUEL_TYPES.ELECTRIC]: 'electricity',
  [FUEL_TYPES.GAS]: 'gas'
};

export const METER_TYPES = {
  SMETS1: 'SMETS1',
  SMETS2: 'SMETS2',
  STANDARD: 'standard',
  ECONOMY7: 'e7',
  S1: 'S1', // for gas meter BE returns S1 and s2 instead of smets1 and smets2
  S2: 'S2'
};

export const METER_READ_TYPES = {
  HALF_HOURLY: 'Half Hourly',
  MONTHLY: 'Monthly'
};

export const METER_READ_TYPES_FOR_DISPLAY = {
  [METER_READ_TYPES.HALF_HOURLY]: 'Half-hourly',
  [METER_READ_TYPES.MONTHLY]: 'Monthly'
};

export const TARIFF_TYPES = {
  FIXED: 'Fixed',
  VARIABLE: 'Variable',
  TRACKER: 'Tracker'
};

export const METER_PROFILE_CLS = {
  DOMESTIC_UNRESTRICTED: '01',
  DOMESTIC_E7: '02',
  NON_DOMESTIC_UNRESTRICTED: '03',
  NON_DOMESTIC_E7: '04'
};

export const METER_READS_ERROR_CODES = {
  MeterReadsToleranceException: 'ReadingTooLowOrTooHigh',
  MeterReadsAgentHandoverException: 'HandoffToAgent'
};

export const METER_TYPE_ERROR_CODES = Object.freeze({
  MeterGreenDealException: 'MeterGreenDealException',
  MeterDeenergisedException: 'MeterDeenergisedException',
  MeterProfileClassException: 'MeterProfileClassException',
  MeterRelatedException: 'MeterRelatedException', // TODO: remove this once release-related-meter-acquisition is released
  IneligibleMeterRelatedException: 'IneligibleMeterRelatedException',
  MeterCurrentSupplierNEPException: 'MeterCurrentSupplierNEPException', // Means BG Evolve customer
  MeterCurrentSupplierBGREException: 'MeterCurrentSupplierBGREException', // TODO: check if needed Means BG customer
  MeterEVException: 'MeterEVException', // TODO: check if still needed
  MeterMeasurementClassException: 'MeterMeasurementClassException',
  MHHSUnmeteredSuppliesException: 'MHHSUnmeteredSuppliesException',
  MHHSMeterProfileClassException: 'MHHSMeterProfileClassException'
});

export const UNSPPORTED_METER_ERRORS = [
  METER_TYPE_ERROR_CODES.MeterMeasurementClassException,
  METER_TYPE_ERROR_CODES.MHHSUnmeteredSuppliesException,
  METER_TYPE_ERROR_CODES.MeterProfileClassException,
  METER_TYPE_ERROR_CODES.MHHSMeterProfileClassException,
  METER_TYPE_ERROR_CODES.MeterDeenergisedException,
  METER_TYPE_ERROR_CODES.MeterCurrentSupplierBGREException,
  METER_TYPE_ERROR_CODES.MeterCurrentSupplierNEPException,
  METER_TYPE_ERROR_CODES.IneligibleMeterRelatedException
] as const;

export const MOVE_IN_VERIFY_ERROR_CODES = {
  NotEligibleForMoveInError: 'IneligibleForHomeMove',
  SSDErrorForHomeMove: 'SSDErrorForHomeMove',
  EMDErrorForHomeMove: 'EMDErrorForHomeMove',
  ASDErrorForHomeMove: 'ASDErrorForHomeMove'
};

export const PARTNERS = {
  DIRECT_SALES: routePath.home,
  AWIN: routePath.awin,
  VOICESALES_ACQUISITION: routePath.voicesalesAcquisition,
  VOICESALES_WINBACK: routePath.voiceSalesWinback,
  VOICESALES_HOMEMOVE: routePath.voiceSalesHomeMove,
  NONDOMESTIC_TRANSFER: routePath.nondomesticTransfer,
  REFER_A_FRIEND: routePath.raf,
  QUOTE_BY_EMAIL: routePath.retrieveQuote,
  TARIFF_INFORMATION: routePath.tariffInformation,
  MOVE_IN: routePath.moveIn,
  SOLAR_REGISTRATION: routePath.solarRegistration,
  ABANDONED_CART: routePath.abandonedCart,
  SALE_HANDOFF: routePath.saleHandoff
};

export const LOGIN_REQUIRED = {
  [PARTNERS.DIRECT_SALES]: false,
  [PARTNERS.ABANDONED_CART]: false,
  [PARTNERS.SALE_HANDOFF]: false,
  [PARTNERS.VOICESALES_ACQUISITION]: true,
  [PARTNERS.VOICESALES_WINBACK]: true,
  [PARTNERS.VOICESALES_HOMEMOVE]: true,
  [PARTNERS.NONDOMESTIC_TRANSFER]: true,
  [PARTNERS.QUOTE_BY_EMAIL]: false,
  [PARTNERS.TARIFF_INFORMATION]: false,
  [PARTNERS.MOVE_IN]: false,
  [PARTNERS.SOLAR_REGISTRATION]: false
};

export const PARTNER_MACHINES = {
  [PARTNERS.DIRECT_SALES]: directSalesMachineConfig,
  [PARTNERS.MOVE_IN]: moveInMachineConfig,
  [PARTNERS.VOICESALES_ACQUISITION]: voiceSalesAcquisitionMachineConfig,
  [PARTNERS.VOICESALES_WINBACK]: voiceSalesWinbackMachineConfig,
  [PARTNERS.VOICESALES_HOMEMOVE]: voiceSalesHomeMoveMachineConfig,
  [PARTNERS.NONDOMESTIC_TRANSFER]: nonDomesticTransferMachineConfig,
  [PARTNERS.QUOTE_BY_EMAIL]: quoteByEmailMachineConfig,
  [PARTNERS.TARIFF_INFORMATION]: tariffInformationMachineConfig,
  [PARTNERS.SOLAR_REGISTRATION]: solarRegistrationMachineConfig,
  [PARTNERS.ABANDONED_CART]: abandonedCartMachineConfig,
  [PARTNERS.SALE_HANDOFF]: saleHandoffMachineConfig
};

export const ACCEPTED_PAYMENT_METHOD = {
  [PARTNERS.DIRECT_SALES]: [
    PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT,
    PAYMENT_METHODS.PREPAY
  ],
  [PARTNERS.VOICESALES_ACQUISITION]: [
    PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT,
    PAYMENT_METHODS.CHECK,
    PAYMENT_METHODS.PREPAY
  ],
  [PARTNERS.VOICESALES_WINBACK]: [
    PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT,
    PAYMENT_METHODS.CHECK,
    PAYMENT_METHODS.PREPAY
  ],
  [PARTNERS.VOICESALES_HOMEMOVE]: [
    PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT,
    PAYMENT_METHODS.CHECK,
    PAYMENT_METHODS.PREPAY
  ],
  [PARTNERS.NONDOMESTIC_TRANSFER]: [
    PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT,
    PAYMENT_METHODS.CHECK,
    PAYMENT_METHODS.PREPAY
  ],
  [PARTNERS.MOVE_IN]: [
    PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT,
    PAYMENT_METHODS.CHECK
  ]
};

export const SALES_CHANNELS = {
  [PARTNERS.VOICESALES_ACQUISITION]: 'Voice Sales',
  [PARTNERS.VOICESALES_WINBACK]: 'Voice Sales',
  [PARTNERS.VOICESALES_HOMEMOVE]: 'Voice Sales',
  [PARTNERS.NONDOMESTIC_TRANSFER]: 'Agent Sales',
  [PARTNERS.MOVE_IN]: 'Web Move In'
};

export const SALES_SUBCHANNELS = {
  [PARTNERS.VOICESALES_ACQUISITION]: 'Acquisition',
  [PARTNERS.VOICESALES_WINBACK]: 'Winback',
  [PARTNERS.VOICESALES_HOMEMOVE]: 'Homemove',
  [PARTNERS.AWIN]: 'Affiliates',
  [PARTNERS.REFER_A_FRIEND]: 'refer a friend',
  [PARTNERS.NONDOMESTIC_TRANSFER]: 'Non-Domestic Transfer',
  [PARTNERS.QUOTE_BY_EMAIL]: 'Quote by Email',
  [PARTNERS.MOVE_IN]: ''
};

export const PRODUCT_CODES = {
  DIRECT_SALES: 'DIRECT_ONLINE_DEFAULT',
  [PARTNERS.VOICESALES_ACQUISITION]: 'VOICESALES_ONLINE_DEFAULT',
  [PARTNERS.VOICESALES_WINBACK]: 'VOICESALES_WINBACK_ONLINE_DEFAULT',
  [PARTNERS.VOICESALES_HOMEMOVE]: 'VOICESALES_HOMEMOVE_ONLINE_DEFAULT',
  [PARTNERS.NONDOMESTIC_TRANSFER]: 'NONDOMESTIC_ONLINE_DEFAULT',
  [PARTNERS.MOVE_IN]: 'HOMEMOVE_ONLINE_DEFAULT'
};

export const PARTNER_LOGO = {
  [PARTNERS.VOICESALES_ACQUISITION]: `${logoPath}/logo/agent-sales.svg`,
  [PARTNERS.VOICESALES_WINBACK]: `${logoPath}/logo/agent-sales.svg`,
  [PARTNERS.VOICESALES_HOMEMOVE]: `${logoPath}/logo/agent-sales.svg`,
  [PARTNERS.NONDOMESTIC_TRANSFER]: `${logoPath}/logo/non-domestic-transfer.svg`
};

export const PARTNER_MOBILE_LOGO = {
  [PARTNERS.VOICESALES_ACQUISITION]: `${logoPath}/logo/agent-sales-mobile.svg`,
  [PARTNERS.VOICESALES_WINBACK]: `${logoPath}/logo/agent-sales-mobile.svg`,
  [PARTNERS.VOICESALES_HOMEMOVE]: `${logoPath}/logo/agent-sales-mobile.svg`,
  [PARTNERS.NONDOMESTIC_TRANSFER]: `${logoPath}/logo/non-domestic-transfer-mobile.svg`
};

export const PARTNER_CREDIT_VALUES = {
  [PARTNERS.VOICESALES_WINBACK]: [0, 25, 50, 75, 100, 150]
};

export const HELP_CONTACT_NUMBER = {
  GENERAL: '03301000540',
  GENERAL_LABEL: '0330 100 0540',
  MOVE_IN_PREPAY: '03301000303',
  MOVE_IN_PREPAY_LABEL: '0330 100 0303',
  MOVE_IN: '03301000548',
  MOVE_IN_LABEL: '0330 100 0548',
  HEAT_PUMP: '03301000543',
  HEAT_PUMP_LABEL: '0330 100 0543'
};

export const PHONE_TYPES = {
  LANDLINE_HOME: 'Landline - Home',
  LANDLINE_WORK: 'Landline - Work',
  MOBILE_PERSONAL: 'Mobile - Personal',
  MOBILE_WORK: 'Mobile - Work',
  MAILING_ADDRESS_TEL: 'Mailing Address Telephone',
  FAX: 'Fax',
  TEXTPHONE: 'Textphone',
  WORK_MANAGEMENT: 'Work Management'
};

export const EMAIL_TYPES = {
  PERSONAL: 'Personal',
  WORK: 'Work'
};

export const SALE_TYPES = {
  NON_VERBAL: 'Non - Verbal',
  VERBAL_3RD_PARTY: 'Verbal - 3rd Party',
  VERBAL_AGENT: 'Verbal - Agent',
  ERRONEOUS_TRANSFER: 'Erroneous Transfer',
  NEW_BUSINESS_REGISTRATION: 'New Business Registration'
};

export const SALES_TYPE = {
  [PARTNERS.VOICESALES_ACQUISITION]: SALE_TYPES.VERBAL_AGENT,
  [PARTNERS.VOICESALES_WINBACK]: SALE_TYPES.VERBAL_AGENT,
  [PARTNERS.VOICESALES_HOMEMOVE]: SALE_TYPES.VERBAL_AGENT,
  [PARTNERS.NONDOMESTIC_TRANSFER]: SALE_TYPES.NON_VERBAL
};

export const FMRS = {
  BILLING_CONSENT_DAYS_COUNT: 14,
  MAX_SUPPLY_START_DAYS_COUNT: 28,
  MIN_SUPPLY_START_DAYS_COUNT_PAYG_SMART: 4
};

export const MOVE_IN_DATE = {
  ALLOWED_DAYS_IN_PAST: 90
};

export const VOICESALES_TRACKING_PARAMS = {
  cid_channel: 'voice',
  cid_name: 'acquisition'
};

export const MINIMUM_REQUIRED_AGE = 18;
export const MAXIMUM_REQUIRED_AGE = 120;
export const PRODUCTS_PER_ROW = 4;
export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATE_FORMAT_FOR_DISPLAY = 'DD/MM/YYYY';
export const DIRECT_DEBIT_VERIFICATION_MAX_ATTEMPTS = 3;
export const DIRECT_DEBIT_VERIFICATION_MAX_ERRORS = 4;
export const MOVE_IN_METER_READS_MAX_ATTEMPTS = 3;
export const METER_READS_ELECTRICITY_MAX_DIGITS = 7;
export const METER_READS_GAS_MAX_DIGITS = 6;
export const METER_READS_MIN_DIGITS = 3;
export const METER_READS_DEFAULT_MAX_DIGITS = 6;

/** List of regex */
export const WAF_VALIDATE_ENDPOINT_LIST = [
  /\/premises\/\d+\/services\/?$/,
  /\/tariffs?$/,
  /\/paymentmethods\/directdebits\/verification\/?$/,
  /\/paymentmethods\/directdebits\/validation\/?$/,
  /\/heatpump-signup\/?$/,
  /\/topic-signup\/?$/,
  /\/move-in-verify\/?$/,
  /\/move-in-meter-read\/?$/,
  /\/app-config\/[a-z0-9-]+\/?$/i
];

export const DEVICE_TYPES = {
  MOBILE: 'mobile',
  TABLET: 'tablet',
  DESKTOP: 'desktop'
};

export const AGENT_JOURNEYS_DIRECT_DEBIT_MAX_ATTEMPTS = {
  [PARTNERS.VOICESALES_ACQUISITION]: Number.POSITIVE_INFINITY,
  [PARTNERS.VOICESALES_HOMEMOVE]: Number.POSITIVE_INFINITY,
  [PARTNERS.VOICESALES_WINBACK]: Number.POSITIVE_INFINITY,
  [PARTNERS.NONDOMESTIC_TRANSFER]: Number.POSITIVE_INFINITY
};

export const AEM_PAGES = {
  CrossSellOffer: 'nep-join-promo'
};

export const PREFERRED_COMMUNICATION_METHOD = Object.freeze({
  EMAIL: 'Email',
  POST: 'Paper'
});

export const APP_CONFIG_PROFILES = Object.freeze({
  DISABLE_JOURNEY: 'disable-journey',
  OPERATIONAL_ALERT: 'operational-alert',
  EV_TARIFF_ID: 'ev-tariff-id',
  PRICE_CAP: 'price-cap',
  PRICE_CAP_DD_SAVINGS: 'price-cap-dd-savings',
  PROMOTIONS: 'promotions'
});

export const MASK_CONFIG = {
  mpan: {
    maskCount: 9
  },
  mprn: {
    maskCount: 7
  },
  unmaskedCount: 4,
  maskChar: 'X'
} as const;

export type TAppConfigProfile = typeof APP_CONFIG_PROFILES[keyof typeof APP_CONFIG_PROFILES];
